import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import {
  AdjudicationButton,
  DismissedToggleRow,
  IconGlyph,
  ViewDismissedButton,
} from 'components/inboundReferrals/AdjudicationButton';
import { ActivityInputValues, defaultActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import {
  AdmissionsCriteriaColor,
  AdmissionsCriteriaItem,
  CriteriaCategory,
  CriteriaEvidence,
  groupCriteria,
} from 'components/shared/ai/AdmissionsCriteria';
import type { CitationViewerTarget } from 'components/shared/ai/CitationDocumentViewer';
import { useBesideCitationViewerWidth } from 'components/shared/ai/citationViewerLayout';
import SourceBadges from 'components/shared/ai/SourceBadges';
import { useCitationViewer, WORKFLOW_ATTACHABLE_MODEL } from 'components/shared/ai/useCitationViewer';
import Button from 'components/shared/Button';
import CloseButton from 'components/shared/CloseButton';
import Foldout from 'components/shared/Foldout';
import { mobileTouchExpand, mobileTouchTarget } from 'components/shared/responsiveness/touchTarget';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { simpleDate, simpleDateAtTime } from 'lib/date';
import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';
import { WorkflowActionIntent, workflowQueryKeys, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ChevronRightBold from 'svg/ChevronRightBold';
import NoteIcon from 'svg/NoteIcon';

import { buildingName, buildingTypeNoun, sortedByBuildingName } from '../FanoutPanel';
import { categoryStatus } from '../referralProgress';
import StatusIcon from '../StatusIcon';

import { DRAWER_PADDING } from './notes/NoteRow';
import NotesTab from './notes/NotesTab';
import {
  ClinicalReviewDismissal,
  readClinicalReviewDismissals,
  readClinicalReviewResult,
  reviewsWith,
} from './clinicalReviewData';

const slug = (label: string) => label.toLowerCase().replace(/\s+/g, '-');

// The drawer's top-level tabs. The criteria category filter inside the first tab
// is a second, lower layer — pill buttons, not these underline tabs.
const DRAWER_TABS: TabType[] = [
  { label: 'Clinical Assessment', value: 'assessment' },
  { label: 'Notes', value: 'notes' },
];

/**
 * The deep link a mention email carries: `?actionId=…&tab=notes&activityId=…` on the referral
 * route. Query params rather than path segments, following `?locationEpisodeId=` on the patient
 * page — the drawer's open state stays local instead of being promoted to routed state.
 */
// `kind` is what a step-scoped thread's link adds (InboundReferralPage); cleared here too so a
// closed drawer leaves no stray param behind.
const DEEP_LINK_PARAMS = ['actionId', 'tab', 'activityId', 'kind'];

// Scroll target for a deep-linked row, so a referral with a dozen buildings does not open a
// drawer over an off-screen row.
const reviewRowId = (actionId: string) => `clinical-review-${actionId}`;

// NOTE: requires the API attachment allowlist to include Workflow (HANDOFF-E) — source
// badges render now and become live (openable) once that lands.

// Figma-matched category styling for the clinical criteria (drawer cards + row count pills):
// a faint category tint (`*5`) behind the criteria label, and a dark, readable tone (`*ExtraDark`)
// for the name/pill text. The bright shared accents (colorConfig[*].accent) fail contrast on white
// and on their own faint tints, so both tones come from the Figma "Secondary" palette. (The `blue`
// category maps to the palette's "lightBlue".)
//
// The tint is 5%, matching the design (`rgba(217,63,76,0.05)` on the red card) and the shared
// `colorConfig[*].background` the admissions cards already use — it was `*10` here, which read as
// twice the intended wash.
type CriteriaStyle = { tint: string; text: string };
const CRITERIA_STYLE: Record<AdmissionsCriteriaColor, CriteriaStyle> = {
  red: { tint: colors.red5, text: colors.redExtraDark },
  yellow: { tint: colors.yellow5, text: colors.yellowExtraDark },
  green: { tint: colors.green5, text: colors.greenExtraDark },
  orange: { tint: colors.orange5, text: colors.orangeExtraDark },
  blue: { tint: colors.lightBlue5, text: colors.lightBlueExtraDark },
};

// The row-level "N Red Criteria" pills read one shade darker than the drawer card header above
// (10% vs. CRITERIA_STYLE's 5%) — a pill has to hold its own color on a white row background,
// where the header tint sits directly above a matching-color body.
const CRITERIA_PILL_TINT: Record<AdmissionsCriteriaColor, string> = {
  red: colors.red10,
  yellow: colors.yellow10,
  green: colors.green10,
  orange: colors.orange10,
  blue: colors.lightBlue10,
};

// The API's "last viewed" attribution (nullable) — the reviewed name + formatted timestamp,
// or null when the building's results have never been viewed. Drives the row's "Viewed by …"
// line and the drawer header's "Last Viewed: …" subtitle.
function lastViewed(action: WorkflowAction): { name: string | null; at: string | null } | null {
  if (action.viewsCount <= 0) return null;
  return { name: action.lastViewedByName, at: action.lastViewedAt ? simpleDateAtTime(action.lastViewedAt) : null };
}

// Matched criteria grouped by category via the shared `groupCriteria` — the same grouping + color
// validation the admission criteria drawer uses. `readClinicalReviewResult` has already sorted by
// category `order`, then match strength, then criteria `order`, so `groupCriteria` (which preserves
// first-appearance order) yields categories and criteria in that explicit display order.
//
// No matched-ness filter here: the api publishes only the matched criteria on
// `clinical_review_result`, so every entry is already matched — the same rule
// `admissions/helpers/criteria.ts` follows for the AIJob payload. An empty result IS the
// "No Criteria Found" state, and the count pills, the View affordance, the drawer's filter tabs and
// its body all derive from this function's output.
//
// `dismissed`, when given, leaves those criteria out — which is how every count (row pills, filter
// chips, section headings) comes to exclude them.
function matchedCategoriesFor(
  workflow: Workflow,
  action: WorkflowAction,
  dismissed?: Map<string, ClinicalReviewDismissal>
): CriteriaCategory[] {
  const shown = readClinicalReviewResult(workflow, action).filter((c) => !dismissed?.has(c.aiCriteriaId));
  return groupCriteria(
    shown.map((c) => ({
      categoryId: c.category?.id,
      categoryName: c.category?.name ?? 'Uncategorized',
      categoryColor: c.category?.color,
      criteria: {
        aiCriteriaId: c.aiCriteriaId,
        label: c.label,
        description: c.description || undefined,
        finding: c.finding || undefined,
        matchStrength: c.matchStrength || undefined,
        sourceCount: c.sources.length,
        sources: c.sources.map((s) => ({
          attachmentId: s.attachmentId,
          displayId: s.displayId,
          pages: s.pages,
          highlights: s.highlights,
        })),
      },
    }))
  );
}

// The criteria a building can show, or null when there is no settled result to read — still
// processing, failed, or not yet reached. Lifted out of the row so the fan-out count below reads
// the same rule the row does: an empty array here is the "No Criteria Found" state, which is a
// building with a result and nothing in it.
function readableCategories(
  workflow: Workflow,
  action: WorkflowAction,
  dismissed?: Map<string, ClinicalReviewDismissal>
): CriteriaCategory[] | null {
  const status = action.effectiveStatus;
  if (status === ActionStatus.RUNNING || status === ActionStatus.ERRORED) return null;
  return reviewSettled(action) ? matchedCategoriesFor(workflow, action, dismissed) : null;
}

// A dismiss / undismiss waiting on the reviewer's "all facilities or only this one" answer.
type PendingDismissal = {
  actionId: string;
  criteriaId: string;
  label: string;
  dismiss: boolean;
  otherCount: number;
};

// Body copy for the states that list NO criteria (OLIO-3193). These used to fall through to the
// definition's own description — "Review the clinical admission criteria below…" — which told a
// reviewer to review criteria that were not there, and gave no hint whether the emptiness was a
// failure, a wait, or simply nothing to do. Each one now says why the list is empty and what, if
// anything, happens next.
//
// Keyed on the roll-up categoryStatus produces, which is also what the card's status badge reads —
// so the sentence and the badge are always describing the same state — and, since OLIO-3270, in the
// same words: `skipped` badges as "Not Applicable", which is what the design called the state all
// along.
//
// TOTAL over ActionStatus on purpose, not Partial: a new member is then a compile error here rather
// than a silent fallthrough to the definition's copy. That fallthrough is exactly how `errored`
// shipped reading "review the criteria below" over a failed assessment — the gap was invisible
// because the type permitted it.
//
// The settled states (action_required / completed) carry a sentence too, because "settled" does not
// mean "found something": an assessment can complete having matched nothing. Their entries only ever
// render in that case — `anyCriteriaShown` returns the definition's copy first whenever any building
// still has criteria on screen, which is the only situation in which "review the criteria below" is
// a true statement.
const STATE_BLURBS: Record<ActionStatus, string> = {
  // Flagged — the referral's documents failed to process, so nothing could be assessed.
  [ActionStatus.FLAGGED]:
    'Olio couldn’t process the documents attached to this referral, so no criteria were identified.',
  // Pending — documents are still processing; the assessment fills in on its own.
  [ActionStatus.PENDING]:
    'The documents for this referral are still processing. Admission criteria will appear here automatically once they’re ready.',
  // Running — the documents are done and the assessment ITSELF is generating. Distinct from
  // pending, which is still waiting on the documents, so the two states name different waits.
  [ActionStatus.RUNNING]:
    'This assessment is still being generated. Admission criteria will appear here automatically once it’s complete.',
  // Not Applicable — no documents were ever shared, so there was nothing to assess. Reversible:
  // adding documents later re-opens the step, which is why the copy promises it.
  [ActionStatus.SKIPPED]:
    'No documents have been shared for this referral, so there are no admission criteria to review. Criteria will be identified automatically if documents are added later.',
  // Errored — the assessment itself failed to generate. Distinct from flagged, which is the
  // DOCUMENTS failing upstream; here the documents were fine and our own run broke. The errored
  // rows each carry a Retry link, which is what "below" points at.
  [ActionStatus.ERRORED]:
    'Olio ran into a problem generating this assessment, so no criteria were identified. You can retry it below.',
  // Canceled — the work was stopped and is not coming back, so unlike every other state here the
  // copy promises nothing further.
  [ActionStatus.CANCELED]: 'This assessment was canceled, so no admission criteria were identified.',
  // Settled with nothing found. Reached only when no building has criteria (see above), so this is
  // the "assessment ran and matched nothing" card, not one whose criteria are simply listed below.
  // Ready for Review and Complete say the same thing here: what the reader needs is that the run
  // happened and came back empty, which is a different fact from it never having run.
  [ActionStatus.ACTION_REQUIRED]:
    'Olio reviewed the documents for this referral and didn’t identify any admission criteria to flag.',
  [ActionStatus.COMPLETED]:
    'Olio reviewed the documents for this referral and didn’t identify any admission criteria to flag.',
};

// The blurb above the building list: the state's own copy where it has one, else the definition's
// description as before.
//
// `anyCriteriaShown` is the guard against a blurb contradicting the rows underneath it. The blurb is
// ONE sentence over a roll-up; the criteria are PER BUILDING. categoryStatus ranks errored first, so
// two SNFs where one assessment failed and the other completed with matches would otherwise print
// "…so no criteria were identified" directly above a row listing them. Every sentence in
// STATE_BLURBS asserts an absence, so none of them may render while any building still has criteria
// on screen — that case returns the definition's copy, which is true again precisely because there
// IS something to review. That same guard is what lets the settled states carry a sentence at all.
//
// The gate is "no building has criteria", not "no building has a settled result". A building that
// settled with an EMPTY result has a result but nothing to show, and it must not drag the card back
// to "review the criteria below" over an empty list.
//
// There used to be a carve-out here for a step badged Flagged only because its inputs had changed,
// which would have told the reader Olio couldn't process the documents when nothing had failed.
// The engine redoes that work itself now (OLIO-3374) and staleness no longer feeds the roll-up, so
// a Flagged roll-up always comes from a genuinely flagged building and the copy is honest again.
function reviewBlurb(actions: WorkflowAction[], anyCriteriaShown: boolean): string | undefined {
  const defined = actions[0]?.description ?? undefined;
  if (anyCriteriaShown) return defined;

  return STATE_BLURBS[categoryStatus(actions)];
}

// The clinical review CONTENTS (Figma #8/#9/#10): a short blurb, an "SNFs (N)" count, then
// one clean row per building — no per-row status icon and no divider chrome (the shared
// FanoutPanel's icons/borders read as clutter here). Each row is name + a status line
// (matched-criteria pills, "No Criteria Found", "Processing…", or "Failed to process") with
// a View/Retry affordance on the right.
export default function ClinicalReviewPanel({
  actions,
  workflow,
  onInvoke,
  isInvoking,
  onView,
  onSaveProgress,
  isSavingProgress,
}: {
  actions: WorkflowAction[];
  workflow: Workflow;
  onInvoke: (id: string, intent: WorkflowActionIntent) => void;
  isInvoking: boolean;
  onView: (actionId: string) => void;
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput, options?: { applyToAllGroups?: boolean }) => void;
  isSavingProgress: boolean;
}) {
  const ordered = sortedByBuildingName(actions);
  const { canEditAction } = useWorkflowPermissions();
  const [pending, setPending] = useState<PendingDismissal | null>(null);

  // The modal's count is the set the API's "apply to all" changes: other assessments here that
  // matched this criterion and hold it in the other state. None → nothing to ask, act at once.
  // The API refuses "apply to all" outright when any other assessment the reviewer can see is one
  // they can't edit (apply_to_all_facilities.rb#authorize!), so then it isn't offered either.
  const toggleDismissal = (action: WorkflowAction, item: AdmissionsCriteriaItem, dismiss: boolean) => {
    const id = item.aiCriteriaId;
    if (isSavingProgress || !id) return;

    const others = ordered.filter((other) => other.id !== action.id);
    const otherCount = others.every((other) => canEditAction(other, workflow))
      ? others.filter(
          (other) =>
            readClinicalReviewResult(workflow, other).some((c) => c.aiCriteriaId === id) &&
            readClinicalReviewDismissals(other).has(id) !== dismiss
        ).length
      : 0;

    if (otherCount === 0) onSaveProgress(action.id, reviewsWith(action, id, dismiss));
    else setPending({ actionId: action.id, criteriaId: id, label: item.label, dismiss, otherCount });
  };

  // Built at confirm time, not when the modal opened: the write carries the whole dismissal list, so
  // a list captured earlier would undo any dismissal another reviewer saved while the modal was open.
  const resolvePending = (applyToAllGroups: boolean) => {
    const action = pending && actions.find((candidate) => candidate.id === pending.actionId);
    setPending(null);
    if (!pending || !action) return;
    onSaveProgress(action.id, reviewsWith(action, pending.criteriaId, pending.dismiss), { applyToAllGroups });
  };
  // The buildings a fanned-out note can actually reach. The API targets only assessments that
  // settled WITH criteria — a reader opens a building's thread off its result, so one that is
  // unassessed, still processing, failed, or found nothing has no way in and receives no copy.
  // Counting every sibling instead would offer the checkbox where the note has nowhere else to go,
  // and promise a footer count the reader could never open.
  //
  // It doubles as the blurb's guard: "some building has criteria on screen" is the same fact, so the
  // sentence above the list and the note fan-out below it are reading one number, not two rules that
  // could drift apart.
  const reachableCount = ordered.filter((action) => (readableCategories(workflow, action) ?? []).length > 0).length;
  const blurb = reviewBlurb(ordered, reachableCount > 0);

  return (
    <ReviewBody data-testid='fanout-panel'>
      {blurb && <ReviewBlurb>{blurb}</ReviewBlurb>}
      <div>
        {/* A lone building needs no count heading — the single row speaks for itself. */}
        {ordered.length > 1 && (
          <ReviewCount>
            {/* "SNFs (4)" — the buildings' shared type noun, capitalized as a heading; a mixed or
                untyped set falls back to "Facilities". Same all-agree rule as the header caption. */}
            {buildingTypeNoun(ordered, 'Facilities')} ({ordered.length})
          </ReviewCount>
        )}
        <ReviewList $scroll={ordered.length > 4}>
          {ordered.map((action) => {
            // Presentation reads effectiveStatus (the API-derived view of status), per the
            // convention effectiveStatus introduced — ClinicalReview doesn't diverge it today, so
            // this matches status, but keeps one rule for what the FE renders.
            const running = action.effectiveStatus === ActionStatus.RUNNING;
            const errored = action.effectiveStatus === ActionStatus.ERRORED;
            const viewed = lastViewed(action);
            // Matched criteria for a settled building — grouped once here and shared by the
            // status line, the View affordance, and the drawer (null while in-flight/failed).
            const matchedCategories = readableCategories(workflow, action);
            const dismissals = readClinicalReviewDismissals(action);
            const standingCategories = readableCategories(workflow, action, dismissals);
            return (
              // One row layout for every state (Figma node 10610): an optional leading status
              // icon (spinner / amber warning) for in-flight and failed buildings, the name +
              // state stacked in the middle, and a right-aligned action (View / Retry) — all
              // vertically centered against each other. Settled buildings have no icon, so
              // their names sit flush-left.
              <ReviewRow
                key={action.id}
                id={reviewRowId(action.id)}
                data-testid='fanout-row'
                data-group-id={action.groupId ?? undefined}>
                {(running || errored) && (
                  <StatusIconCell>
                    <StatusIcon indicator={running ? 'running' : 'error'} size={14} />
                  </StatusIconCell>
                )}
                <ReviewLeadMain $tight={running || errored} data-testid='review-lead'>
                  <ReviewName>{buildingName(action)}</ReviewName>
                  {running && <StatusLine $color={colors.purple100}>Processing…</StatusLine>}
                  {errored && <StatusLine $color={colors.red100}>Error</StatusLine>}
                  {matchedCategories && standingCategories && (
                    <ReviewStatus matchedCategories={matchedCategories} standingCategories={standingCategories} />
                  )}
                  {viewed && (
                    <ViewedLine>
                      Viewed{viewed.name ? ` by ${viewed.name}` : ''}
                      {viewed.at ? ` on ${viewed.at}` : ''}
                    </ViewedLine>
                  )}
                </ReviewLeadMain>
                {errored && canEditAction(action, workflow) ? (
                  /* A reader still sees the row read "Error" beside its building; only the re-run needs the grant,
                     matching ErroredPrompt. View below stays ungated either way — opening the
                     assessment is reading it. */
                  <RetryAffordance>
                    <RetryLink
                      type='button'
                      data-testid='review-retry'
                      disabled={isInvoking}
                      onClick={() => onInvoke(action.id, 'rerun')}>
                      Retry
                    </RetryLink>
                  </RetryAffordance>
                ) : !errored && matchedCategories && matchedCategories.length > 0 ? (
                  // Matched criteria to show → a View link into the drawer. "No Criteria
                  // Found" (empty matchedCategories) gets no affordance.
                  <ReviewView
                    action={action}
                    workflow={workflow}
                    matchedCategories={matchedCategories}
                    // The exact count, not an estimate: the same set the server resolves as the
                    // fan-out's targets, arrived at from the same pair of facts (settled, and
                    // carrying criteria) that its `accepts_fanned_out_notes?` reads.
                    groupCount={reachableCount}
                    onView={onView}
                    dismissals={dismissals}
                    onToggleDismissal={
                      canEditAction(action, workflow)
                        ? (item, dismiss) => toggleDismissal(action, item, dismiss)
                        : undefined
                    }
                    isSavingProgress={isSavingProgress}
                  />
                ) : null}
              </ReviewRow>
            );
          })}
        </ReviewList>
      </div>
      {pending && (
        <ConfirmDismissalModal pending={pending} onCancel={() => setPending(null)} onConfirm={resolvePending} />
      )}
    </ReviewBody>
  );
}

function ConfirmDismissalModal({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: PendingDismissal;
  onCancel: () => void;
  onConfirm: (applyToAllGroups: boolean) => void;
}) {
  const verb = pending.dismiss ? 'Dismiss' : 'Undismiss';
  const options = [
    { label: `${verb} for All`, value: 'all' },
    { label: `${verb} Only This Assessment`, value: 'one' },
  ];
  const [scope, setScope] = useState(options[0]);
  const facilities = `${pending.otherCount} additional ${pending.otherCount === 1 ? 'facility' : 'facilities'}`;
  const confirmLabel = `Confirm ${pending.dismiss ? 'Dismissal' : 'Undismissal'}`;

  // Portaled into a layer above the citation viewer. The viewer is itself portaled to <body> at the
  // nested-Foldout level (1100), and StandardModal's own backdrop sits at 901 — just above the
  // assessment drawer (900) — so without this the viewer covered the confirm's left half and took
  // its clicks. Portaled rather than only raised, so no ancestor's stacking context can cap it.
  return createPortal(
    <AboveCitationViewer>
      <StandardModal title={confirmLabel} onCancel={onCancel}>
        <ModalText>
          <ModalCriteria>Criteria: {pending.label}</ModalCriteria>
          <ModalCopy data-testid='confirm-dismissal-copy'>
            This criteria was {pending.dismiss ? 'identified' : 'dismissed'} for {facilities} associated to this
            referral. Do you want to {verb.toLowerCase()} it for just this clinical assessment or for all?
          </ModalCopy>
        </ModalText>
        <ScopeToggle role='radiogroup'>
          {options.map((option) => (
            <ScopeOption
              key={option.value}
              type='button'
              role='radio'
              aria-checked={option.value === scope.value}
              $selected={option.value === scope.value}
              onClick={() => setScope(option)}>
              {option.label}
            </ScopeOption>
          ))}
        </ScopeToggle>
        <Actions>
          <Button variant='ghost' type='button' onClick={onCancel} data-testid='confirm-dismissal-cancel'>
            Cancel
          </Button>
          <Button type='button' onClick={() => onConfirm(scope.value === 'all')} data-testid='confirm-dismissal-submit'>
            {confirmLabel}
          </Button>
        </Actions>
      </StandardModal>
    </AboveCitationViewer>,
    document.body
  );
}

// Same lift HighCostMedListSlideout and AdmissionSlideout give their confirms.
const AboveCitationViewer = styled.div`
  position: relative;
  z-index: 1200;
`;

// Whether the AI has settled a building's result — matches found (action_required, awaiting
// the coordinator's review) or nothing to review (completed: no matches, or already reviewed).
// Until then (running / errored / pending) there's no criteria outcome to show.
function reviewSettled(action: WorkflowAction): boolean {
  return action.isActionRequired || action.isCompleted;
}

// The settled row's second line: the per-building criteria outcome. (Processing and failed
// states have their own leading-icon layout in the panel.)
function ReviewStatus({
  matchedCategories,
  standingCategories,
}: {
  matchedCategories: CriteriaCategory[];
  standingCategories: CriteriaCategory[];
}) {
  if (matchedCategories.length === 0) return <StatusLine $color={colors.black50}>No Criteria Found</StatusLine>;
  if (standingCategories.length === 0) return <StatusLine $color={colors.black50}>All criteria dismissed</StatusLine>;
  return (
    <CountRow>
      {standingCategories.map((cat) => (
        <CriteriaCountPill
          key={cat.id ?? cat.label}
          $bg={CRITERIA_PILL_TINT[cat.color]}
          $text={CRITERIA_STYLE[cat.color].text}
          data-testid={`criteria-count-${slug(cat.label)}`}>
          {cat.criteria.length} {cat.label} Criteria
        </CriteriaCountPill>
      ))}
    </CountRow>
  );
}

function ReviewView({
  action,
  workflow,
  matchedCategories,
  groupCount,
  onView,
  dismissals,
  onToggleDismissal,
  isSavingProgress,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  matchedCategories: CriteriaCategory[];
  groupCount: number;
  onView: (actionId: string) => void;
} & DismissalProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * A mention email lands here. `actionId` is compared against this row's action, so exactly one
   * drawer opens — and an id that matches nothing opens none, which is what a re-run between the
   * email and the click looks like: the show payload renders only current actions, so the reader
   * is simply on the right referral with nothing open. Degraded, never an error.
   */
  const deepLinked = searchParams.get('actionId') === action.id;

  /**
   * The arrival is spent once the note has been reached. Held here rather than in NotesTab, and not
   * read off the URL: the params stay put until the drawer closes, and the tab unmounts every time
   * the reader looks at the assessment — so the tab cannot remember this for itself, and coming
   * back would page the thread forward again and yank the scroll to a note already read.
   */
  const [focusSpent, setFocusSpent] = useState(false);
  const focusActivityId = deepLinked && !focusSpent ? searchParams.get('activityId') : null;

  const [open, setOpen] = useState(deepLinked);
  // Shrinks beside the citation viewer while it is open, rather than sitting underneath it.
  const drawerWidth = useBesideCitationViewerWidth('min(92%, 960px)');
  // selectedTab lives here rather than in ReviewDrawer so the subscription below can tell
  // whether this action's Notes tab is on screen.
  //
  // Out here it also survives a close, so reopening this building lands on the tab it was left
  // on — deliberate, and the same call as the draft below. Resetting to the assessment while
  // keeping the draft would hide a half-typed note behind a tab the reader has to find again.
  // Scoped per building, so a different row still opens on the assessment.
  const [selectedTab, setSelectedTab] = useState<TabType>(
    (deepLinked && DRAWER_TABS.find((tab) => tab.value === searchParams.get('tab'))) || DRAWER_TABS[0]
  );
  // The notes composer's draft lives out here rather than in NotesTab. The drawer swaps tabs by
  // conditional render, and Foldout unmounts its children when closed, so anything held further
  // in is destroyed by a glance at the assessment tab or a mis-click on the backdrop. Out here it
  // survives both, and stays scoped to this building.
  const [noteDraft, setNoteDraft] = useState(defaultActivityInputValues);
  // Lifted for the same reason the draft is, and kept out of ActivityInputValues because that type
  // is shared with the patient story, where "all groups" means nothing.
  const [sendToAll, setSendToAll] = useState(false);
  const { profile } = useProfile();
  const queryClient = useQueryClient();

  const notesOpen = open && selectedTab.value === 'notes';

  /**
   * The row icon renders with the slideout shut, so this subscription has to outlive the
   * drawer — unlike NotesTab's, which is scoped to that tab's mount. useWebsocketSubscription
   * ref-counts by serialized identifier, so when both are live they share one ActionCable
   * subscription and each handler is called.
   *
   * One subscription per rendered clinical review, because every channel in this codebase is
   * keyed to a single record id. That is one per facility under consideration — single digits.
   */
  useWebsocketSubscription<{ id: string; activities?: boolean; actorId: string | null }>({
    channel: 'WorkflowActionChannel',
    params: { workflow_action_id: action.id },
    onReceived: (data) => {
      // Your own notes never count as unread.
      if (data.actorId === profile?.id) return;
      // The subscribe-time transmit is { id, actorId } with no association key.
      if (!data.activities) return;
      // The note is already on screen, and NotesTab's invalidation refetches page 1 — which
      // stamps the arriving note read. Flipping the flag here would light an icon that is wrong
      // the instant it appears.
      if (notesOpen) return;

      queryClient.setQueryData<Workflow>(
        workflowQueryKeys.show(workflow.id),
        (prev) => prev?.withUnreadActivities(action.id) ?? prev
      );
    },
  });

  // Opening the drawer marks the building's results viewed (POSTs a view; the refetch updates
  // the "Viewed by" attribution).
  const openDrawer = () => {
    onView(action.id);
    setOpen(true);
  };

  /**
   * Arriving by deep link: bring the row on screen behind the drawer, and record the view exactly
   * as openDrawer does. The clinical review is open either way, and how it was opened should not
   * change what "open" means for its lifecycle.
   *
   * Mount only — arrival is a one-time event, and re-running when the params clear would re-record
   * a view for a drawer the reader has just shut. The ref survives StrictMode's double invocation,
   * which would otherwise record every development arrival twice.
   */
  const viewRecorded = useRef(false);
  useEffect(() => {
    if (!deepLinked || viewRecorded.current) return;
    viewRecorded.current = true;

    onView(action.id);
    document.getElementById(reviewRowId(action.id))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Closing clears the params, or the next render reopens what was just dismissed. Watches `open`
   * rather than wrapping one handler, so both ways out — the X and the backdrop — are covered.
   */
  useEffect(() => {
    if (open || !deepLinked) return;

    const next = new URLSearchParams(searchParams);
    DEEP_LINK_PARAMS.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [open, deepLinked, searchParams, setSearchParams]);

  return (
    <>
      <ViewAffordance>
        {/* Immediately left of "View", on the same baseline. Rows with nothing unread render
            the link alone, with no reserved gap. */}
        {action.hasNewActivities && (
          <NoteIcon color={colors.blue100} width={12} height={13} data-testid='review-unread-notes' />
        )}
        <ViewLink type='button' onClick={openDrawer} data-testid='review-view'>
          View
          <ChevronRightBold width={6} height={10} />
        </ViewLink>
      </ViewAffordance>
      {/* Wide panel per the Figma (#9/#12) — the criteria cards need the room; the default
          Foldout width (360px) reads as a cramped strip. */}
      <Foldout
        open={open}
        setOpen={setOpen}
        closeOnBackdropClick
        width={drawerWidth}
        style={{ transition: 'transform 0.5s, width 0.5s' }}
        mobileFullWidth
        data-testid='review-slideout'>
        <ReviewDrawer
          action={action}
          workflow={workflow}
          matchedCategories={matchedCategories}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          noteDraft={noteDraft}
          setNoteDraft={setNoteDraft}
          groupCount={groupCount}
          sendToAll={sendToAll}
          setSendToAll={setSendToAll}
          focusActivityId={focusActivityId}
          onFocusHandled={() => setFocusSpent(true)}
          open={open}
          onClose={() => setOpen(false)}
          dismissals={dismissals}
          onToggleDismissal={onToggleDismissal}
          isSavingProgress={isSavingProgress}
        />
      </Foldout>
    </>
  );
}

function ReviewDrawer({
  action,
  workflow,
  matchedCategories,
  selectedTab,
  setSelectedTab,
  noteDraft,
  setNoteDraft,
  groupCount,
  sendToAll,
  setSendToAll,
  focusActivityId,
  onFocusHandled,
  open,
  onClose,
  dismissals,
  onToggleDismissal,
  isSavingProgress,
}: DismissalProps & {
  action: WorkflowAction;
  workflow: Workflow;
  matchedCategories: CriteriaCategory[];
  // Owned by ReviewView so the row's channel handler can see which tab is open.
  selectedTab: TabType;
  setSelectedTab: (tab: TabType) => void;
  noteDraft: ActivityInputValues;
  setNoteDraft: Dispatch<SetStateAction<ActivityInputValues>>;
  groupCount: number;
  sendToAll: boolean;
  setSendToAll: Dispatch<SetStateAction<boolean>>;
  focusActivityId?: string | null;
  onFocusHandled: () => void;
  // The drawer's own open state: its content stays mounted while it slides shut.
  open: boolean;
  onClose: () => void;
}) {
  const viewed = lastViewed(action);

  // The blue note icon rides the tab's affix slot, so it stays visible from the Assessment tab.
  // Same mark and same boolean as the row icon — one flag, two surfaces. NoteIcon is the bare
  // glyph; ActivityNoteIcon is the same mark inside a tinted badge, which is the patient-story
  // activity-row treatment and not what this indicator wants.
  const tabs = DRAWER_TABS.map((tab) =>
    tab.value === 'notes' && action.hasNewActivities
      ? {
          ...tab,
          affix: <NoteIcon color={colors.blue100} width={12} height={13} data-testid='notes-tab-unread' />,
        }
      : tab
  );

  return (
    <SlideoutBody>
      <DrawerHeader>
        <div>
          <DrawerTitle>Clinical Assessment &ndash; {buildingName(action)}</DrawerTitle>
          {viewed && (
            <DrawerSubtitle>
              Last Viewed:{viewed.name ? ` ${viewed.name}` : ''}
              {viewed.at ? ` at ${viewed.at}` : ''}
            </DrawerSubtitle>
          )}
        </div>
        <PositionedClose onClick={onClose} data-testid='review-close' />
      </DrawerHeader>

      <DrawerTabs>
        <Tabs tabs={tabs} onTabClick={setSelectedTab} selectedTab={selectedTab} />
      </DrawerTabs>

      <TabBody>
        {selectedTab.value === 'assessment' ? (
          // Each tab owns its own overflow: both keep their controls fixed and scroll only the
          // list beneath them.
          <AssessmentTab
            matchedCategories={matchedCategories}
            dismissals={dismissals}
            onToggleDismissal={onToggleDismissal}
            isSavingProgress={isSavingProgress}
            attachments={workflow.attachments}
            drawerOpen={open}
          />
        ) : (
          <NotesTab
            workflowId={workflow.id}
            closedToNotes={workflow.isLocked || workflow.isArchived}
            action={action}
            groupCount={groupCount}
            values={noteDraft}
            setValues={setNoteDraft}
            sendToAll={sendToAll}
            setSendToAll={setSendToAll}
            focusActivityId={focusActivityId}
            onFocusHandled={onFocusHandled}
          />
        )}
      </TabBody>
    </SlideoutBody>
  );
}

type DismissalProps = {
  dismissals: Map<string, ClinicalReviewDismissal>;
  // Absent when the reviewer may not dismiss here (no edit grant): no buttons.
  onToggleDismissal?: (item: AdmissionsCriteriaItem, dismiss: boolean) => void;
  isSavingProgress: boolean;
};

const isDismissed = (dismissals: Map<string, ClinicalReviewDismissal>, item: AdmissionsCriteriaItem) =>
  !!item.aiCriteriaId && dismissals.has(item.aiCriteriaId);

// The criteria content, and the document viewer its citations open. Its category filter is a second,
// lower tab layer inside the first top-level tab. Counts and headings read the standing criteria
// only; dismissed ones sit behind View Dismissed.
function AssessmentTab({
  matchedCategories,
  dismissals,
  onToggleDismissal,
  isSavingProgress,
  attachments,
  drawerOpen,
}: {
  matchedCategories: CriteriaCategory[];
  attachments: Workflow['attachments'];
  drawerOpen: boolean;
} & DismissalProps) {
  const [filter, setFilter] = useState<string>('all'); // 'all' | category label
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());

  const categories = matchedCategories.map((cat) => ({
    ...cat,
    key: cat.id ?? cat.label,
    standing: cat.criteria.filter((item) => !isDismissed(dismissals, item)),
    dismissed: cat.criteria.filter((item) => isDismissed(dismissals, item)),
  }));
  const total = categories.reduce((n, c) => n + c.standing.length, 0);
  const visible = filter === 'all' ? categories : categories.filter((c) => c.label === filter);

  // A PDF citation opens in the left-hand viewer, scrolled to and outlining the evidence,
  // instead of a new browser tab at its first page.
  const { openViewer, viewer } = useCitationViewer({
    attachmentType: WORKFLOW_ATTACHABLE_MODEL,
    parentOpen: drawerOpen,
  });

  const toggleRevealed = (key: string) =>
    setRevealed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const renderCard = (cat: CriteriaCategory, item: AdmissionsCriteriaItem, key: string) => (
    <CriteriaCardView
      key={key}
      color={cat.color}
      item={item}
      dismissal={item.aiCriteriaId ? dismissals.get(item.aiCriteriaId) : undefined}
      onToggleDismissal={onToggleDismissal}
      isSavingProgress={isSavingProgress}
      attachments={attachments}
      onOpenSource={openViewer}
    />
  );

  return (
    <AssessmentLayout>
      <FilterTabs data-testid='review-filter-tabs'>
        <FilterTab
          type='button'
          $active={filter === 'all'}
          data-testid='review-filter-all'
          onClick={() => setFilter('all')}>
          All ({total})
        </FilterTab>
        {categories.map((cat) => (
          <FilterTab
            key={cat.key}
            type='button'
            $active={filter === cat.label}
            data-testid={`review-filter-${slug(cat.label)}`}
            onClick={() => setFilter(cat.label)}>
            {cat.label} ({cat.standing.length})
          </FilterTab>
        ))}
      </FilterTabs>
      <DrawerDivider />
      <CriteriaScrollArea data-testid='criteria-list'>
        {visible.map((cat) => {
          const shown = revealed.has(cat.key);
          return (
            <CriteriaGroup key={cat.key} data-testid={`criteria-group-${slug(cat.label)}`}>
              <GroupHeading>
                {cat.label} Criteria ({cat.standing.length})
              </GroupHeading>
              {cat.dismissed.length > 0 && (
                <DismissedBlock>
                  {cat.dismissed.map((item, i) => {
                    const dismissal = item.aiCriteriaId ? dismissals.get(item.aiCriteriaId) : undefined;
                    return (
                      <DismissalSummary key={item.aiCriteriaId ?? i} data-testid='criteria-dismissal-summary'>
                        ‘{item.label}’ was dismissed
                        {dismissal?.dismissedAt ? ` on ${simpleDate(dismissal.dismissedAt)}` : ''}
                        {dismissal?.dismissedByName ? ` by ${dismissal.dismissedByName}` : ''}
                      </DismissalSummary>
                    );
                  })}
                  <DismissedToggleRow>
                    <ViewDismissedButton
                      type='button'
                      variant='outline-primary'
                      onClick={() => toggleRevealed(cat.key)}
                      data-testid={`review-toggle-dismissed-${slug(cat.label)}`}>
                      {shown ? 'Hide Dismissed Criteria' : 'View Dismissed Criteria'}
                    </ViewDismissedButton>
                  </DismissedToggleRow>
                </DismissedBlock>
              )}
              {shown && cat.dismissed.map((item, i) => renderCard(cat, item, `dismissed-${i}`))}
              {cat.standing.map((item, i) => renderCard(cat, item, String(i)))}
            </CriteriaGroup>
          );
        })}
      </CriteriaScrollArea>
      {viewer}
    </AssessmentLayout>
  );
}

function CriteriaCardView({
  color,
  item,
  dismissal,
  onToggleDismissal,
  isSavingProgress,
  attachments,
  onOpenSource,
}: {
  color: AdmissionsCriteriaColor;
  item: AdmissionsCriteriaItem;
  dismissal?: ClinicalReviewDismissal;
  onToggleDismissal?: (item: AdmissionsCriteriaItem, dismiss: boolean) => void;
  isSavingProgress: boolean;
  attachments: Workflow['attachments'];
  onOpenSource: (target: CitationViewerTarget) => void;
}) {
  const style = CRITERIA_STYLE[color];

  return (
    <CriteriaCard $dismissed={!!dismissal} data-testid='criteria-card'>
      <CriteriaHeader $tint={style.tint} $divided={Boolean(item.description)}>
        <CriteriaLabel $accent={style.text}>{item.label}</CriteriaLabel>
        <SourceBadges sources={item.sources ?? []} attachments={attachments} onOpen={onOpenSource} />
        {onToggleDismissal && item.aiCriteriaId && (
          <DismissButton
            type='button'
            variant='outline-gray'
            disabled={isSavingProgress}
            onClick={() => onToggleDismissal(item, !dismissal)}
            data-testid={`criteria-dismiss-${item.aiCriteriaId}`}>
            <IconGlyph aria-hidden>✕</IconGlyph>
            {dismissal ? 'Undismiss' : 'Dismiss'}
          </DismissButton>
        )}
      </CriteriaHeader>
      {item.description && <CriteriaDescription>{item.description}</CriteriaDescription>}
      {/* The design rules off the evidence from the criteria body above it. When there is no
          description this divider is the ONLY rule under the header — which is why the header
          itself is divided on the description alone. Dividing it on the strength too would
          stack its border against this one into a double line. */}
      {item.matchStrength && (
        <>
          <CriteriaDivider />
          <CriteriaEvidenceRow>
            <CriteriaEvidence matchStrength={item.matchStrength} finding={item.finding} />
          </CriteriaEvidenceRow>
        </>
      )}
    </CriteriaCard>
  );
}

const ReviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

const ReviewBlurb = styled(BodySmall)`
  font-size: 14px;
  line-height: 18px;
  color: ${colors.black100};
`;

const ReviewCount = styled(BodySmallBold)`
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  color: ${colors.black100};
`;

// With more than 4 buildings the list scrolls in place (Figma #10) rather than growing the
// card unbounded; the max-height shows ~4 rows so the 5th peeks to signal there's more.
const ReviewList = styled.div<{ $scroll: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $scroll }) =>
    $scroll &&
    `
      max-height: 296px;
      overflow-y: auto;
    `}
`;

// Rows are separated by a light gray rule (Figma #10). One centered flex layout for all
// states: [optional status icon] [name + state] [right-aligned action]. The icon and action
// stay vertically centered against the stacked name/state in the middle.
const ReviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 0;

  & + & {
    border-top: 1px solid ${colors.black10};
  }

  /* One column on a phone: the row carries a name, a status line, a wrapping set of criteria
     count pills and an action, which is more than fits beside each other at 375px. The status
     icon stays on the name's line — it is the name's own state — and the action takes the line
     below. Unlike the Facility Decision rows, whose Accept/Deny are the point of the row and
     hold their own column, this one's View is a way in rather than the decision itself. */
  @media ${({ theme }) => theme.devices.mobile} {
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

// In-flight and failed rows stack their name and state line tighter (4px) than a settled
// row's name-over-criteria (8px), per Figma.
const ReviewLeadMain = styled.div<{ $tight: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $tight }) => ($tight ? '4px' : '8px')};
  min-width: 0;
  flex: 1;
`;

// A fixed-size cell for the leading status icon so the spinner and the error glyph occupy the
// same footprint (and names indent by the same amount).
const StatusIconCell = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const ReviewName = styled(BodySmallBold)`
  font-size: 14px;
  line-height: 16px;
  color: ${colors.black100};
`;

const StatusLine = styled(BodySmall)<{ $color: string }>`
  font-size: 12px;
  line-height: 16px;
  color: ${({ $color }) => $color};
`;

// The per-row "last viewed" attribution (Figma) — a muted note under a reviewed building's
// criteria (see lastViewed()).
const ViewedLine = styled(BodySmall)`
  font-size: 11px;
  line-height: 16px;
  color: ${colors.black50};
`;

// A bare button reset shared by the row's View / Retry links (Figma text-links: blue, no
// underline).
const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  cursor: pointer;
`;

// The unread-note icon and the View link sit on one baseline. No gap is reserved when there is
// no icon — the link renders alone, exactly as it did before.
const ViewAffordance = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media ${({ theme }) => theme.devices.mobile} {
    flex-basis: 100%;
  }
`;

// The review View link (Figma), with a small chevron after the label. Flex-centering the
// chevron against the descender-less "View" leaves it reading a touch high, so nudge it down
// 1px to optically center on the text.
const ViewLink = styled(LinkButton)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;

  svg {
    transform: translateY(1px);
  }

  /* Height rather than a centred pseudo-element: the link has its own line here, so growing it
     costs nothing, while a 44px band centred on a 16px link reaches 14px up — past the row's 8px
     gap and over the criteria pills above it, where a tap would open the drawer instead. */
  ${mobileTouchTarget}
`;

// The retry action for a failed review (Figma node 10610 — text-link blue, no destructive
// treatment), matching the View affordance.
const RetryLink = styled(LinkButton)`
  font-size: 12px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${mobileTouchTarget}
`;

// Mirrors ViewAffordance: the wrapper takes the line, the control keeps its own width. Putting
// the full-width basis on the button instead turned the entire line into a tap target for a
// re-run of the AI assessment, which is not a thing to fire by accident.
const RetryAffordance = styled.div`
  display: flex;
  align-items: center;

  @media ${({ theme }) => theme.devices.mobile} {
    flex-basis: 100%;
  }
`;

const CountRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CriteriaCountPill = styled.span<{ $bg: string; $text: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $text }) => $text};
`;

// Fills the fixed-height Foldout exactly, so the drawer itself never scrolls — the header and
// tab row stay put and each tab owns its own scrolling region. `overflow: hidden` is what stops
// Foldout's own `overflow-y: auto` (Foldout.tsx:82) from taking over and moving the whole panel.
const SlideoutBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;

  /* Published as a custom property rather than a literal, because several children cancel this
     padding with a negative margin to run full-bleed and have to carry whatever it currently is.
     Declaring it here means they follow it automatically instead of each repeating the number —
     including NotesTab's list, which is shared with the Benefit Verification slideout. Both
     drawers gutter at 24px; they part company below the breakpoint, where this one narrows and
     that one does not. */
  --drawer-gutter: ${DRAWER_PADDING}px;
  padding: var(--drawer-gutter);

  /* 24px on each side costs an eighth of a 375px screen, and the criteria cards are the content
     the drawer exists for. */
  @media ${({ theme }) => theme.devices.mobile} {
    --drawer-gutter: 16px;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

// The underline tab row sits under the header, spanning edge-to-edge past the body padding.
const DrawerTabs = styled.div`
  border-bottom: 1px solid ${colors.black10};
  margin: 0 calc(-1 * var(--drawer-gutter)) 20px;
  padding: 0 var(--drawer-gutter);
  flex-shrink: 0;
`;

// The remaining space below the tabs. `min-height: 0` is load-bearing: without it a flex child
// refuses to shrink below its content, and the scroll moves back up to the drawer.
const TabBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// The category filter and the divider under it stay put; only the criteria scroll. Same shape
// as the notes tab, where the composer stays put and only the thread moves.
const AssessmentLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// `min-height: 0` is what confines the scrollbar here rather than letting it escape upward and
// take the filter row with it.
const CriteriaScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

// Full-bleed past the body's 24px padding. Safe only because this sits OUTSIDE
// CriteriaScrollArea: `overflow-y: auto` forces overflow-x to compute to `auto` too, so inside
// that box these margins would hang 24px past each edge and add a horizontal scrollbar.
const DrawerDivider = styled.hr`
  margin: 16px calc(-1 * var(--drawer-gutter)) 20px;
  border: none;
  border-top: 1px solid ${colors.black10};
  flex-shrink: 0;
`;

const DrawerTitle = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black100};
`;

// The drawer's "Last Viewed" attribution under the title (Figma) — small and muted
// (see lastViewed()).
const DrawerSubtitle = styled(BodySmall)`
  margin-top: 4px;
  font-size: 11px;
  color: ${colors.black50};
`;

const PositionedClose = styled(CloseButton)`
  flex-shrink: 0;

  &:hover {
    color: ${colors.black100};
  }
`;

const FilterTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;

  /* Sideways rather than wrapping, matching the drawer's own tab strip above it: wrapped, a run
     with several categories builds a block of filters above the criteria someone opened the
     drawer to read. Scrolling keeps the list's starting position the same whatever the run holds.
     The strip is named in the specs' intentional-scroller list, since a scroller is exactly what
     this is. */
  @media ${({ theme }) => theme.devices.mobile} {
    flex-wrap: nowrap;
    overflow-x: auto;
    margin: 0 calc(-1 * var(--drawer-gutter));
    padding: 0 var(--drawer-gutter);
  }
`;

// Tab buttons for the drawer's category filter (Figma #12): bordered rounded rects; the
// active tab reads primary-blue (border + light tint + blue text), inactive is a neutral
// gray outline on white.
const FilterTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? colors.blue100 : colors.black25)};
  background: ${({ $active }) => ($active ? colors.blue10 : colors.white)};
  color: ${({ $active }) => ($active ? colors.blue100 : colors.black100)};
`;

// A criteria section in the drawer ("Red Criteria (2)" etc.), grouped by category. Cards
// within a group sit 16px apart; groups 28px apart (Figma #12).
const CriteriaGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & + & {
    margin-top: 28px;
  }
`;

const GroupHeading = styled(BodySmallBold)`
  font-size: 12px;
  color: ${colors.black100};
`;

// A single criteria card (Figma #12): a color-tinted header carrying the criteria label
// (in the category's dark tone) + its supporting-source badges, a gray divider, then a
// white description body. The drawer is built on the 12px label scale, not the 14px body
// scale the workflow card uses.
const CriteriaCard = styled.div<{ $dismissed: boolean }>`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
  opacity: ${({ $dismissed }) => ($dismissed ? 0.6 : 1)};
`;

// Pushed to the header's far edge, past the citation badges.
const DismissButton = styled(AdjudicationButton)`
  margin-left: auto;
  flex-shrink: 0;

  /* The pill's outline is the design, so the area grows and the box does not. The rule goes on
     this local wrapper rather than on AdjudicationButton itself, which the auto-check reviews
     also use. */
  ${mobileTouchExpand}
`;

const DismissedBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > :last-child {
    margin-top: 8px;
  }
`;

const DismissalSummary = styled(BodySmall)`
  text-align: center;
  font-size: 12px;
  line-height: 14px;
  color: ${colors.black50};
`;

const ModalText = styled.div`
  margin-top: 16px;
`;

const ModalCriteria = styled(BodySmallBold)`
  display: block;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.black100};
`;

const ModalCopy = styled(BodySmall)`
  display: block;
  margin-top: 20px;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.black100};
`;

const ScopeToggle = styled.div`
  display: flex;
  margin: 24px 0 40px;
  padding: 2px;
  height: 40px;
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  background: ${colors.white};

  /* The fixed height has to give way: nothing here clips, so taller options would simply paint
     outside the toggle's own rounded border. */
  @media ${({ theme }) => theme.devices.mobile} {
    height: auto;
  }
`;

const ScopeOption = styled.button<{ $selected: boolean }>`
  flex: 1;
  border: none;
  border-radius: 3px;
  font-size: 12px;
  line-height: 12px;
  font-weight: ${({ $selected }) => ($selected ? FONT_WEIGHT_MEDIUM : 'normal')};
  color: ${({ $selected }) => ($selected ? colors.blue100 : colors.black100)};
  background: ${({ $selected }) => ($selected ? colors.blue10 : 'transparent')};
  cursor: pointer;

  /* This one answers a question before a decision that cannot be taken back from here, so it
     grows to the touch minimum rather than keeping its drawn size. */
  ${mobileTouchTarget}
`;

const CriteriaHeader = styled.div<{ $tint: string; $divided: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;

  /* A criteria label, its source badges and Dismiss do not fit on one line at 375px, and none of
     the three can give up width — the label wraps, the rest follow it down. */
  @media ${({ theme }) => theme.devices.mobile} {
    flex-wrap: wrap;
  }
  background: ${({ $tint }) => $tint};
  border-bottom: ${({ $divided }) => ($divided ? `1px solid ${colors.black10}` : 'none')};
`;

const CriteriaLabel = styled.span<{ $accent: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $accent }) => $accent};
`;

const CriteriaDescription = styled(BodySmall)`
  padding: 16px;
  font-size: 12px;
  color: ${colors.black100};
  line-height: 20px;
`;

// Holds the shared Match Strength disclosure, inset to match CriteriaDescription so the caret lines
// up under the criteria body text. Only rendered when there is a strength to show, so it needs no
// empty-state collapse.
const CriteriaEvidenceRow = styled.div`
  padding: 14px 16px;
`;

const CriteriaDivider = styled.hr`
  margin: 0;
  border: none;
  border-top: 1px solid ${colors.black10};
`;
